import type { MapColourMode } from '@tla/types';

/** Linear interpolation between two hex colours. */
function mix(a: [number, number, number], b: [number, number, number], t: number): string {
  const r = Math.round(a[0] + (b[0] - a[0]) * t);
  const g = Math.round(a[1] + (b[1] - a[1]) * t);
  const bl = Math.round(a[2] + (b[2] - a[2]) * t);
  return `rgb(${r},${g},${bl})`;
}

const RED: [number, number, number] = [255, 70, 70];
const GREEN: [number, number, number] = [34, 240, 110];
const GREY: [number, number, number] = [120, 130, 140];
const BLUE: [number, number, number] = [74, 163, 255];
const YELLOW: [number, number, number] = [251, 191, 36];

function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}

/**
 * Map a channel value to a colour for the racing-line rendering, depending on
 * the selected colour mode:
 * - longitudinal_g: red (braking) → grey (0) → green (accelerating), ±1.2 G.
 * - brake: grey → red across 0..100%.
 * - speed: blue (slow) → yellow → red (fast) across the value range.
 */
export function colourFor(
  mode: MapColourMode,
  value: number,
  range: [number, number],
): string {
  if (!Number.isFinite(value)) return '#555';
  switch (mode) {
    case 'longitudinal_g': {
      const g = Math.max(-1.2, Math.min(1.2, value));
      // Reach full colour by ~0.8 G and apply a perceptual boost so gentle
      // acceleration/braking is already clearly tinted rather than near-grey.
      if (g < 0) return mix(GREY, RED, clamp01(-g / 0.8) ** 0.6);
      return mix(GREY, GREEN, clamp01(g / 0.8) ** 0.6);
    }
    case 'brake':
      return mix(GREY, RED, clamp01(value / 100));
    case 'speed':
    default: {
      const [lo, hi] = range;
      const t = hi > lo ? clamp01((value - lo) / (hi - lo)) : 0;
      return t < 0.5 ? mix(BLUE, YELLOW, t * 2) : mix(YELLOW, RED, (t - 0.5) * 2);
    }
  }
}

/** Channel id that drives a given colour mode. */
export function channelForMode(mode: MapColourMode): string {
  switch (mode) {
    case 'brake':
      return 'brake';
    case 'speed':
      return 'speed_gps';
    case 'longitudinal_g':
    default:
      return 'longitudinal_acc';
  }
}
